"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import { WifiOff, MonitorPlay, ArrowUpRight } from "lucide-react";

const PAIRS = [
  {
    slug: "works-offline",
    icon: WifiOff,
    label: { en: "Works Offline", es: "Funciona Offline" },
    desc: {
      en: "WiFi dies at 7 PM. Cash sales keep ringing, saved on the device, and sync the moment you reconnect.",
      es: "El WiFi muere a las 7 PM. Las ventas en efectivo siguen, guardadas en el dispositivo, y se sincronizan al reconectar.",
    },
  },
  {
    slug: "kitchen-display-system",
    icon: MonitorPlay,
    label: { en: "Kitchen Display (KDS)", es: "Kitchen Display (KDS)" },
    desc: {
      en: "What the rush feels like in the kitchen. Every cook gets their station, not the whole ticket.",
      es: "Cómo se siente la hora pico en la cocina. Cada cocinero ve su estación, no el ticket completo.",
    },
  },
];

export default function RushHourPairs() {
  const { t } = useLang();

  return (
    <section className="relative py-24 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1080px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="text-[11px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
            {t("Pairs well with", "Combina bien con")}
          </div>
          <h2 className="text-2xl md:text-[36px] font-black tracking-tight text-redy-charcoal">
            {t("Speed + uptime. Inside and out.", "Velocidad + uptime. Adentro y afuera.")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PAIRS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
              >
                <Link
                  href={`/features/${p.slug}`}
                  className="group block bg-white rounded-[24px] p-7 border border-black/5 hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div
                      className="w-12 h-12 rounded-[14px] flex items-center justify-center group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: "rgba(244, 114, 43, 0.12)" }}
                    >
                      <Icon className="w-5 h-5" style={{ color: "#D7263D" }} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-redy-secondary/40 group-hover:text-redy-charcoal group-hover:rotate-12 transition-all" />
                  </div>
                  <div className="text-[18px] font-black text-redy-charcoal mb-2">
                    {t(p.label.en, p.label.es)}
                  </div>
                  <div className="text-[14px] font-medium text-redy-secondary leading-relaxed">
                    {t(p.desc.en, p.desc.es)}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
